import { Injectable } from '@angular/core';

export interface Category {
  id: string;
  name: string;
  description: string;
  allowedRoles: string[];
  isCitySpecific: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  
  // Definir todas las categorías disponibles
  private readonly categories: Category[] = [
    {
      id: 'politica',
      name: 'Política',
      description: 'Noticias relacionadas con política nacional e internacional',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'economia',
      name: 'Economía',
      description: 'Noticias económicas y financieras',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'deportes',
      name: 'Deportes',
      description: 'Noticias deportivas nacionales e internacionales',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'mundo',
      name: 'Mundo',
      description: 'Noticias internacionales',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'opinion',
      name: 'Opinión',
      description: 'Artículos de opinión y editoriales',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'historias',
      name: 'Historias',
      description: 'Historias humanas y reportajes especiales',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'ciudad',
      name: 'Ciudad',
      description: 'Noticias locales específicas de cada ciudad',
      allowedRoles: ['admin', 'editor'], // Los editores generales NO pueden acceder a esta categoría
      isCitySpecific: true
    },
    {
      id: 'principales',
      name: 'Principales',
      description: 'Noticias destacadas y principales',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'actualidad',
      name: 'Actualidad',
      description: 'Noticias de actualidad y últimas noticias',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    },
    {
      id: 'ind.arg',
      name: 'IND.ARG',
      description: 'Noticias relacionadas con industria argentina',
      allowedRoles: ['admin', 'general_editor'],
      isCitySpecific: false
    }
  ];

  constructor() {}

  /**
   * Obtiene todas las categorías disponibles
   */
  getAllCategories(): Category[] {
    return [...this.categories];
  }

  /**
   * Obtiene las categorías disponibles para un rol específico
   */
  getCategoriesForRole(role: string): Category[] {
    return this.categories.filter(category => 
      category.allowedRoles.includes(role)
    );
  }

  /**
   * Obtiene las categorías que NO están permitidas para un rol específico
   */
  getRestrictedCategoriesForRole(role: string): Category[] {
    return this.categories.filter(category => 
      !category.allowedRoles.includes(role)
    );
  }

  /**
   * Verifica si una categoría está permitida para un rol específico
   */
  isCategoryAllowedForRole(categoryId: string, role: string): boolean {
    const category = this.categories.find(cat => cat.id === categoryId);
    return category ? category.allowedRoles.includes(role) : false;
  }

  /**
   * Obtiene una categoría específica por ID
   */
  getCategoryById(categoryId: string): Category | undefined {
    return this.categories.find(cat => cat.id === categoryId);
  }

  /**
   * Obtiene las categorías específicas de ciudad
   */
  getCitySpecificCategories(): Category[] {
    return this.categories.filter(category => category.isCitySpecific);
  }

  /**
   * Obtiene las categorías generales (no específicas de ciudad)
   */
  getGeneralCategories(): Category[] {
    return this.categories.filter(category => !category.isCitySpecific);
  }

  /**
   * Obtiene los nombres de las categorías como array de strings
   */
  getCategoryNames(): string[] {
    return this.categories.map(cat => cat.name);
  }

  /**
   * Obtiene los IDs de las categorías como array de strings
   */
  getCategoryIds(): string[] {
    return this.categories.map(cat => cat.id);
  }

  /**
   * Verifica si un usuario puede crear/editar artículos en una categoría específica
   */
  canUserManageCategory(userRole: string, categoryId: string): boolean {
    return this.isCategoryAllowedForRole(categoryId, userRole);
  }

  /**
   * Obtiene las categorías que un editor general puede gestionar
   */
  getGeneralEditorCategories(): Category[] {
    return this.getCategoriesForRole('general_editor');
  }

  /**
   * Obtiene las categorías que un editor de ciudad puede gestionar
   */
  getCityEditorCategories(): Category[] {
    return this.getCategoriesForRole('editor');
  }

  /**
   * Obtiene las categorías que un administrador puede gestionar
   */
  getAdminCategories(): Category[] {
    return this.getCategoriesForRole('admin');
  }
}